<template>
  <v-card>
    <v-card-title>{{ $i18n.useChangeOwnership.ADD_OWNER }}</v-card-title>
    <v-divider></v-divider>
    <v-card-text v-if="ownershipChangeStatus" @closeModal="closeModal()" class="pt-4">
      <v-form ref="changeOwnershipForm">
        <template v-if="ownershipChangeStatus.canChange">
          <v-row>
            <v-col>
            <p class="all-caps-header mb-2">{{ $i18n.useChangeOwnership.CLIENT }}</p>
            <client-badge
                :hide-actions="true"
                :client="client"
                :readonly="true"/>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="pt-3">
            <v-divider  v-if="$vuetify.breakpoint.smAndUp"></v-divider>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <p class="all-caps-header mb-2 error--text">{{ $i18n.useChangeOwnership.CURRENT_OWNER }}</p>
            <v-row>
              <v-col>
                <user-badge
                    :user="owner"
                    :readonly="true"/>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="ml-3">
                <v-icon>mdi-arrow-down</v-icon>
              </v-col>
            </v-row>
            <p class="all-caps-header mb-2 success--text">{{ $i18n.useChangeOwnership.NEW_OWNER }}</p>
            <v-row>
              <v-col>
                <user-autocomplete
                  v-if="ableToDelegateToOtherUser"
                  :filter="usersFilter"
                  :clearAfterSelect="false"
                  v-model="futureOwnerId" :label="$i18n.useChangeOwnership.NEW_OWNER"
                  :required="true"
                  :ownershipChange="true"
                  :itemText="'displayNameWithOrganisationAndEmail'"
                  />
                <user-badge
                  v-else
                  :user="$user"
                  :readonly="true"/>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="shrink">
            <v-icon class="mt-5 mr-2">mdi-message-text-outline</v-icon>
          </v-col>
          <v-col>
            <v-textarea hide-details v-model="note" :label="$i18n.useChangeOwnership.OWNER_REASON" outlined rows="2"/>
            <fp-checkbox
                v-if="!ownershipChangeStatus.requireSms"
                class="mt-3"
                v-model="clientAgreement"
                :label="$i18n.useChangeOwnership.CLIENT_AGREED_TO_ADD_OWNER"/>
          </v-col>
        </v-row>
        <template v-if="ownershipChangeStatus.requireSms">
          <v-divider class="my-4"></v-divider>
          <v-row>
            <v-col cols="12" md="7">
              <p class="body-1 primary-text-color font-weight-medium">{{ $i18n.useChangeOwnership.REQUIRES_AUTHORIZATION }}</p>
              <p>
                {{ $i18n.useChangeOwnership.SEND_SMS_CODE}}
              </p>
              <v-icon>mdi-phone</v-icon>
              <span class="body-1">{{ phoneFormat(client.phone) }}</span>
            </v-col>
            <v-col cols="12" md="5">
              <v-card>
                <v-card-text>
                  <sms-code-check
                      :smsApiData="clientLoginData"
                      :additionalParams="smsAdditionalParams"/>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </template>
      </template>
      <v-row v-else>
        <v-col class="shrink">
          <v-avatar color="grey" class="lighten-2" size="32px">
            <v-icon>
              mdi-face-agent
            </v-icon>
          </v-avatar>
        </v-col>
        <v-col>
          {{$i18n.useChangeOwnership.CLIENT_DEALS_NOT_ALLOW}}
        </v-col>
      </v-row>
      </v-form>
    </v-card-text>
    <fp-loader v-else></fp-loader>
    <v-divider></v-divider>
    <v-card-actions v-if="ownershipChangeStatus">
      <template v-if="ownershipChangeStatus.canChange">
        <fp-btn
            :disabled="changeOwnershipDisabled"
            :click="() => changeOwnership()"
            primary>
          {{ $i18n.useChangeOwnership.ADD_OWNER_LOWER_CASE }}
        </fp-btn>
        <v-spacer></v-spacer>
        <fp-btn @click="closeModal()">{{ $i18n.useButtonCommons.CANCEL }}</fp-btn>
      </template>
      <fp-btn
          v-else
          @click="closeModal()"
          primary>
        {{$i18n.useChangeOwnership.UNDERSTAND}}
      </fp-btn>
    </v-card-actions>
  </v-card>
</template>
<script lang="ts">
import {Component} from 'vue-property-decorator';
import {VueModalComponent} from '@/components/fpComponents/services/ModalService/VueModalComponent';
import {ChangeOwnershipData, OwnershipChangeStatus} from '@/modules/client/models/OwnershipStatus';
import ClientApi from '@/modules/client/services/ClientApi';
import Client from '@/models/Client';
import UserBadge from '@/components/badges/UserBadge.vue';
import SmsCodeCheck from '@/components/commons/SmsCodeCheck.vue';
import AuthService from '@/modules/user/AuthService';
import {SmsApiData} from '@/models/LoginData';
import FpSelect from '@/components/fpComponents/FpSelect.vue';
import {UserFilter} from '@/models/Filter';
import { UserListElement } from '@/models/ppl/UserListElement';
import UserAutocomplete from '@/components/inputs/autocompletes/UserAutocomplete.vue';
import UserApi from '@/modules/user/services/UserApi';
import { phoneFormat } from '@/composables/useFilters';

@Component({
  components: {FpSelect, SmsCodeCheck, UserBadge, UserAutocomplete,},
})
export default class ChangeOwnership extends VueModalComponent<number> {
  public phoneFormat = phoneFormat;

  client: Client = this.modalData.client;
  owner: Client = this.modalData.owner || null;
  futureOwnerId: Nullable<number> = null;
  note?: string = undefined;
  clientAgreement: boolean = false;
  ownershipChangeStatus: Nullable<OwnershipChangeStatus> = null;
  clientLoginData: SmsApiData = {
    smsCode: '',
    phone: this.modalData.client.phone,
  };

  usersFilter: UserFilter = new UserFilter();
  ownershipSearchText: string = '';

  async fetchUsersForOwnership(filter: UserFilter): Promise<UserListElement[]> {
    const response = await UserApi.searchOwnershipChangeUsers(filter);
    return response.items as unknown as UserListElement[];
  }

  async onOwnershipSearch(value: string) {
    this.ownershipSearchText = value;
  }

  mounted() {
    this.fetchOwnershipChangeStatus();
    this.onOwnershipSearch('');
    if (this.$user.isAdminOrVerifier) {
      this.usersFilter.organisationIds = [this.$user.organisationId!,];
    }

    if (this.$user.isChiefOfDivision && !this.$user.isAdminOrVerifier) {
      this.usersFilter.divisionIds = this.$user.divisionsChief?.map(x => x.id);
    }
  }

  get ableToDelegateToOtherUser(): boolean {
    return this.$user.isAdminOrVerifier || this.$user.isChiefOfDivision;
  }

  /**
   * Wymusza szablon SMS-a z informacją o zmianie eksperta (zamiast samego kodu weryfikacyjnego).
   * Treść (PL/EN/RO) oraz wybór języka wg preferencji klienta obsługuje backend.
   */
  get smsAdditionalParams(): Record<string, string> {
    return { templateName: 'clientOwnershipChange' };
  }

  get changeOwnershipDisabled(): boolean {
    return !this.ownershipChangeStatus ||
        (!this.ownershipChangeStatus.requireSms && !this.clientAgreement) ||
        (this.ownershipChangeStatus.requireSms && this.clientLoginData.smsCode.length !== 6);
  }

  private async changeOwnership() {
    const userId = this.ableToDelegateToOtherUser ? this.futureOwnerId! : AuthService.User!.id!;
    const clientId = this.client.id!;
    const data: ChangeOwnershipData = {userId, clientId, notes: this.note,};
    if (this.ownershipChangeStatus?.requireSms) {
      Object.assign(data, this.clientLoginData);
    }
    if (this.validate(data) && this.$validate('changeOwnershipForm')) {
      try {
        await ClientApi.changeOwnership(clientId, data);
        this.closeModal(userId);
      } catch (e) {
        this.errorHandler(e, this.$i18n.useChangeOwnership.CHANGE_OWNERSHIP_ERROR_HANDLER);
      }
    }
  }

  private async fetchOwnershipChangeStatus() {
    try {
      this.ownershipChangeStatus = await ClientApi.getOwnershipStatus(this.client.id);
    } catch (e) {
      this.errorHandler(e, this.$i18n.useChangeOwnership.FETCH_OWNERSHIP_CHANGE_STATUS_ERROR_HANDLER);
    }
  }

  private validate(data: ChangeOwnershipData): boolean {
    const MIN_CHAR: number = 10 as const;
    if (!data.notes || data.notes.length <= MIN_CHAR) {
      this.$snackbarService.openErrorSnackbar(
        this.$i18nFormatter(
          this.$i18n.useChangeOwnership.VALIDATE_ERROR_SNACKBAR, {minChar: MIN_CHAR,}));
      return false;
    }
    return true;
  }
}
</script>
